import { useState, useRef } from "react";
import { Box, IconButton, Typography, Stack, Divider } from "@mui/material";
// icons
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
// redux
import { useDispatch, useSelector } from "../../../redux/store";
import { setSelectedContent } from "../../../redux/slices/creator";
// config
import colors from "../../../config/colors";
import { formatNumberKMB, getAssetUrl } from "../../../utils/helpers";

const ContentPreview = ({ previewRef }) => {
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  const dispatch = useDispatch();
  const selectedContent = useSelector((state) => state.creator.selectedContent);
  const views = selectedContent?.total_views ?? selectedContent?.views ?? 0;
  const likes = selectedContent?.total_likes ?? selectedContent?.likes ?? 0;
  const comments =
    selectedContent?.total_comments ?? selectedContent?.comments ?? 0;
  const shares = selectedContent?.total_shares ?? selectedContent?.shares ?? 0;
  const saves = selectedContent?.total_saved ?? selectedContent?.saved ?? 0;
  const reach = selectedContent?.total_reach ?? selectedContent?.reach ?? 0;
  const placeholder = getAssetUrl("Placeholder.png");

  const performanceMetrics = [
    { label: "Views", value: views },
    { label: "Likes", value: likes },
    { label: "Comments", value: comments },
    { label: "Shares", value: shares },
    { label: "Saves", value: saves },
    { label: "Reach", value: reach },
  ];
  const getMediaFromUrls = (mediaUrlsString) => {
    try {
      if (!mediaUrlsString) return null;
      const parsedUrls = JSON.parse(mediaUrlsString);
      return Array.isArray(parsedUrls) ? parsedUrls[0] : null;
    } catch (error) {
      console.error("Error parsing media_urls:", error);
      return null;
    }
  };
  const media = getMediaFromUrls(selectedContent?.media_urls);
  const mediaUrl = media?.url || selectedContent?.media_url || null;

  const mediaType =
    (
      media?.type ||
      selectedContent?.media_type ||
      selectedContent?.post_type
    )?.toLowerCase() || null;

  const handleClose = () => {
    dispatch(setSelectedContent(null));
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVideoLoad = () => {
    if (videoRef.current) {
      setIsPlaying(!videoRef.current.paused);
    }
  };

  const handleViewOnInstagram = () => {
    window.open(
      selectedContent?.permalink || selectedContent?.content_link,
      "_blank",
    );
  };

  if (!selectedContent) return null;
  return (
    <Box
      ref={previewRef}
      sx={{ gap: "16px", display: "flex", flexDirection: "row" }}
    >
      <Box
        position="relative"
        sx={{
          width: "273px",
          height: "410px",
          borderRadius: "4px",
          background: colors.gray100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <Box
          position={"absolute"}
          top={"8px"}
          right={"8px"}
          zIndex={1000}
          sx={{ cursor: "pointer" }}
        >
          <CloseIcon
            data-cy="content-preview-close-button"
            sx={{ cursor: "pointer" }}
            onClick={handleClose}
          />
        </Box>
        <Box position="absolute" bottom="8px" left="8px" zIndex={999}>
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            bgcolor={colors.gray200}
            p={0.5}
            borderRadius={1}
            onClick={handleViewOnInstagram}
            sx={{ cursor: "pointer" }}
          >
            <Typography fontSize={10} color="white">
              View on instagram
            </Typography>
            <OpenInNewIcon sx={{ fontSize: 10, color: "white" }} />
          </Stack>
        </Box>

        {mediaType === "video" && mediaUrl ? (
          <Box
            position="relative"
            width="100%"
            height="100%"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <video
              ref={videoRef}
              src={mediaUrl}
              autoPlay
              loop
              playsInline
              muted={isMuted}
              onLoadedData={handleVideoLoad}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              style={{
                width: "273px",
                height: "100%",
                objectFit: "cover",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={togglePlayPause}
            />

            {/* Play/Pause Icon */}
            <Box
              position="absolute"
              top="50%"
              left="50%"
              zIndex={999}
              sx={{
                transform: "translate(-50%, -50%)",
                opacity: isPlaying ? 0 : 0.8,
                transition: "opacity 0.3s ease",
                cursor: "pointer",
              }}
              onClick={togglePlayPause}
            >
              {!isPlaying && (
                <IconButton
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.8)",
                    },
                  }}
                >
                  <PlayArrowIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </Box>

            {/* Mute/Unmute Icon */}
            <Box position="absolute" bottom="16px" right="16px" zIndex={999}>
              <IconButton
                onClick={toggleMute}
                sx={{
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                }}
              >
                {isMuted ? (
                  <VolumeOffIcon sx={{ fontSize: 16 }} />
                ) : (
                  <VolumeUpIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>
          </Box>
        ) : (
          <img
            src={
              selectedContent?.thumbnail_url ||
              selectedContent?.media_url ||
              placeholder
            }
            alt={selectedContent?.post_id}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        )}
      </Box>
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: "16px", flex: 1 }}
      >
        <Stack direction="column" gap={1}>
          <Typography
            fontSize={12}
            fontWeight={500}
            lineHeight="18px"
            color={colors.gray600}
          >
            Performance
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "8px",
            }}
          >
            {performanceMetrics.map((metric, index) => (
              <Stack key={index} direction="column">
                <Typography fontSize={24} fontWeight={500} lineHeight="32px">
                  {formatNumberKMB(metric.value)}
                </Typography>
                <Typography fontSize={12} fontWeight={400} lineHeight="18px">
                  {metric.label}
                </Typography>
              </Stack>
            ))}
          </Box>
        </Stack>
        <Divider />
        <Stack direction="column" gap={1}>
          <Typography
            fontSize={12}
            fontWeight={400}
            lineHeight="18px"
            sx={{
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",

              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 10,

              maxHeight: "180px",
            }}
          >
            {selectedContent?.caption}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default ContentPreview;
